import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRobot, 
  faComments, 
  faTools, 
  faChartLine, 
  faLightbulb, 
  faRocket,
  faQuestionCircle,
  faMagic,
  faDatabase,
  faChartBar
} from '@fortawesome/free-solid-svg-icons';
import ChatContent from './content/ChatContent';
import ChatDiagnostics from './ChatDiagnostics';
import AIProviderSelector from './AIProviderSelector';
import ChatProvider from '../../providers/ChatProvider';

const ChatPageContent = () => {
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [currentProvider, setCurrentProvider] = useState('openai');
  const [currentModel, setCurrentModel] = useState('gpt-4o-mini');
  const [showExamples, setShowExamples] = useState(true);

  const exampleQuestions = [
    {
      category: "🎯 Análisis de Leads",
      questions: [
        "¿Cuántos leads generamos con Ford este mes?",
        "¿Cuál es el CPL promedio de UTPL?",
        "¿Qué cliente tiene mejor eficiencia en leads?"
      ]
    },
    {
      category: "💰 Inversión & ROI", 
      questions: [
        "¿Cuánto hemos gastado con Claro los últimos 3 meses?",
        "¿Cuáles son las campañas más rentables?",
        "¿Dónde debería invertir más dinero?"
      ]
    },
    {
      category: "📊 Rendimiento",
      questions: [
        "¿Cuál es el CTR promedio de mis campañas?",
        "¿Qué plataforma genera mejores resultados?",
        "¿Cómo está el rendimiento vs el mes pasado?"
      ]
    }
  ];

  return (
    <Container fluid className="py-4">
      {/* Header con mejor diseño */}
      <Row className="justify-content-center mb-4">
        <Col xl={12}>
          <div className="text-center mb-4">
            <div className="d-inline-flex align-items-center justify-content-center bg-primary bg-gradient rounded-circle mb-3" 
                 style={{ width: '80px', height: '80px' }}>
              <FontAwesomeIcon icon={faRobot} className="text-white" size="2x" />
            </div>
            <h1 className="mb-2">🚀 Galaxy AI Assistant</h1>
            <p className="lead text-muted mb-3">
              Tu consultor experto en datos publicitarios
            </p>
            <div className="d-flex justify-content-center gap-2 mb-3">
              <Badge bg="success" className="px-3 py-2">
                <FontAwesomeIcon icon={faDatabase} className="me-1" />
                Datos en Tiempo Real
              </Badge>
              <Badge bg="warning" className="px-3 py-2">
                <FontAwesomeIcon icon={faMagic} className="me-1" />
                Análisis Inteligente
              </Badge>
              <Badge bg="info" className="px-3 py-2">
                <FontAwesomeIcon icon={faRocket} className="me-1" />
                Recomendaciones
              </Badge>
            </div>
          </div>

          {/* Selector de AI y herramientas */}
          <div className="d-flex align-items-center justify-content-between mb-4 p-3 bg-light rounded">
            <div className="d-flex align-items-center gap-3">
              <div>
                <small className="text-muted d-block">Motor de IA:</small>
                <AIProviderSelector
                  currentProvider={currentProvider}
                  currentModel={currentModel}
                  onProviderChange={setCurrentProvider}
                  onModelChange={setCurrentModel}
                />
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowExamples(!showExamples)}
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="me-1" />
                {showExamples ? 'Ocultar' : 'Ver'} Ejemplos
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowDiagnostics(!showDiagnostics)}
              >
                <FontAwesomeIcon icon={faTools} className="me-1" />
                Diagnóstico
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Ejemplos de preguntas */}
      {showExamples && (
        <Row className="justify-content-center mb-4">
          <Col xl={12}>
            <Alert variant="primary" className="border-0 shadow-sm">
              <Alert.Heading className="d-flex align-items-center">
                <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                💡 Ejemplos de preguntas que puedes hacer:
              </Alert.Heading>
              <Row className="mt-3">
                {exampleQuestions.map((category, idx) => (
                  <Col md={4} key={idx} className="mb-3">
                    <h6 className="text-primary mb-2">{category.category}</h6>
                    <ul className="list-unstyled">
                      {category.questions.map((question, qIdx) => (
                        <li key={qIdx} className="mb-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="text-start w-100 border-0 bg-light"
                            style={{ fontSize: '0.9rem' }}
                          >
                            "{question}"
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </Col>
                ))}
              </Row>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Diagnóstico */}
      {showDiagnostics && (
        <Row className="justify-content-center mb-4">
          <Col xl={12}>
            <ChatDiagnostics />
          </Col>
        </Row>
      )}

      {/* Chat principal */}
      <Row className="justify-content-center">
        <Col xl={12}>
          <Card className="shadow border-0">
            <Card.Header className="bg-gradient bg-primary text-white">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faComments} className="me-2" />
                <h5 className="mb-0">Chat con Galaxy AI</h5>
                <Badge bg="success" className="ms-auto">
                  En línea
                </Badge>
              </div>
            </Card.Header>
            <Card.Body className="p-0" style={{ height: '70vh' }}>
              <ChatContent />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Características destacadas */}
      <Row className="mt-5 justify-content-center">
        <Col xl={10}>
          <h4 className="text-center mb-4">🌟 Lo que puedo hacer por ti</h4>
          <Row>
            <Col md={3} className="mb-4">
              <Card className="h-100 border-0 bg-gradient bg-primary text-white text-center">
                <Card.Body>
                  <FontAwesomeIcon icon={faChartLine} className="mb-3" size="3x" />
                  <h5>Análisis de Leads</h5>
                  <p className="small mb-0">
                    CPL, eficiencia, tendencias y recomendaciones específicas
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="h-100 border-0 bg-gradient bg-success text-white text-center">
                <Card.Body>
                  <FontAwesomeIcon icon={faChartBar} className="mb-3" size="3x" />
                  <h5>ROI & Inversión</h5>
                  <p className="small mb-0">
                    Análisis financiero y optimización de presupuestos
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="h-100 border-0 bg-gradient bg-warning text-white text-center">
                <Card.Body>
                  <FontAwesomeIcon icon={faRocket} className="mb-3" size="3x" />
                  <h5>Recomendaciones</h5>
                  <p className="small mb-0">
                    Dónde invertir más, qué optimizar y próximos pasos
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="h-100 border-0 bg-gradient bg-info text-white text-center">
                <Card.Body>
                  <FontAwesomeIcon icon={faDatabase} className="mb-3" size="3x" />
                  <h5>Datos en Vivo</h5>
                  <p className="small mb-0">
                    Consultas directas a tu base de datos en tiempo real
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Tips de uso */}
      <Row className="mt-4 justify-content-center">
        <Col xl={8}>
          <Alert variant="info" className="border-0">
            <Alert.Heading className="h6">
              <FontAwesomeIcon icon={faLightbulb} className="me-2" />
              💡 Tips para mejores resultados:
            </Alert.Heading>
            <ul className="mb-0 small">
              <li><strong>Sé específico:</strong> "Ford leads este mes" mejor que "leads"</li>
              <li><strong>Menciona fechas:</strong> "últimos 3 meses", "este año", etc.</li>
              <li><strong>Incluye clientes:</strong> UTPL, Claro, Ford, Trivo...</li>
              <li><strong>Pide recomendaciones:</strong> "¿dónde debería invertir más?"</li>
            </ul>
          </Alert>
        </Col>
      </Row>
    </Container>
  );
};

const ChatPage = () => {
  return (
    <ChatProvider>
      <ChatPageContent />
    </ChatProvider>
  );
};

export default ChatPage; 